// @ts-strict-ignore
import queryString from 'query-string';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import {apiClient} from 'common/util/api-client';
import {FetchOptions} from 'common/util/shared-client';
import {getLocation} from 'common/util/url';

import {ExpirableCache, MAX_EXPIRY_SECONDS} from './expirable-cache';

export interface QueryOptions {
  /**
   * Force use network and clear response cache.
   * response cache stores processed responses per browser session.
   */
  clearCache?: boolean;
  /**
   * Query from fetch-cache, don't use network.
   * Note: query() will return null promise if match fails. User needs to fallback to network promise.
   */
  fromFetchCache?: boolean;
  /**
   * When the network response comes back, update fetch-cache so it can be queried next time with fromFetchCache.
   */
  updateFetchCache?: boolean;

  /**
   * Cache on any method
   */
  forceCache?: boolean;

  method?: `GET` | `POST` | `PUT` | `PATCH` | `DELETE`;

  contentType?: string;

  abortSignal?: AbortSignal;

  /**
   * HTTP headers to send with the request.
   */
  headers?: Record<string, string>;

  importance?: `low` | `high` | `auto`;
}

/**
 * @deprecated
 *
 * IMPORTANT: extending BaseClient is a deprecated pattern.
 * Use apiClient.get|post|put|patch|delete from an api helper module.
 * It makes code logic easier to understand and simpler.
 * apiClient supports caching via fromFetchCache and updateFetchCache options
 * for memory caching, use a store instead which supports watching when the cache gets updated so ui can be redrawn
 */
export abstract class BaseClient<ResponseType = any> {
  public declare responseCache: ExpirableCache;
  public arrayFormatBracket = false;

  constructor({cacheTtl = MAX_EXPIRY_SECONDS} = {}) {
    this.responseCache = new ExpirableCache(cacheTtl);
  }

  // region To be implemented by inheriting class

  public buildEndpoint(_params = {}, _queryOpts: QueryOptions): string {
    throw new Error(`Must be implemented`);
  }

  public buildRequestBody(_params = {}, _queryOpts: QueryOptions) {
    return null;
  }

  public buildQueryParams(_params = {}, _queryOpts: QueryOptions) {
    return null;
  }

  public processResponse(response, _params?: any): ResponseType {
    return response;
  }

  // endregion

  public getCachedResponse(params) {
    return this.responseCache.get(params);
  }

  public getUrl(params, queryOpts: QueryOptions) {
    let url = this.buildEndpoint(params, queryOpts);
    if (url[0] !== `/`) {
      url = `/` + url;
    }
    const queryParams = this.buildQueryParams(params, queryOpts);
    if (queryParams) {
      if (this.arrayFormatBracket) {
        url += `?${queryString.stringify(queryParams, {arrayFormat: `bracket`})}`;
      } else {
        url += `?${queryString.stringify(queryParams)}`;
      }
    }

    return url;
  }

  public getFetchOptions(params, queryOpts: QueryOptions): FetchOptions {
    const fetchOptions: FetchOptions = pick(queryOpts, [
      `method`,
      `headers`,
      `abortSignal`,
      `fromFetchCache`,
      `updateFetchCache`,
      `importance`,
    ]);

    const body = this.buildRequestBody(params, queryOpts);

    if (!fetchOptions.method) {
      fetchOptions.method = body ? `POST` : `GET`;
    }

    if (queryOpts.contentType) {
      fetchOptions.headers = {
        'Content-Type': queryOpts.contentType,
        'Request-URL': getLocation().href,
      };
    }

    if (body) {
      fetchOptions.body = body;
    }

    return fetchOptions;
  }

  public async query(params: {[key: string]: any}, queryOpts: QueryOptions = {}): Promise<ResponseType> {
    const filteredParams = omit(params, [`useQueryCache`, `forceCache`]);
    const filteredQueryOpts = omit(queryOpts, [`clearCache`, `forceCache`]);
    const cacheParams = {...filteredParams, ...filteredQueryOpts};

    const fetchOptions: FetchOptions = this.getFetchOptions(params, queryOpts);
    if (!queryOpts.fromFetchCache && fetchOptions.method === `GET`) {
      if (queryOpts.clearCache) {
        this.responseCache.clear(cacheParams);
      } else {
        if (this.responseCache.has(cacheParams)) {
          return this.responseCache.get(cacheParams);
        }
      }
    }

    const url = this.getUrl(params, queryOpts);

    const dataType = params.format === `csv` ? `text` : `json`;

    // eslint-disable-next-line local-rules/deprecated-member-access
    return apiClient.fetch(url, fetchOptions).then((response) => {
      return (
        response &&
        response.status !== 204 &&
        response[dataType]().then((innerResponse) => {
          // fetch cache miss will be returned as null
          if (innerResponse === null) {
            return innerResponse;
          }
          if (dataType === `json`) {
            // Copy over status for use by clients.
            innerResponse.statusCode = response.status;
          }
          const processed = this.processResponse(innerResponse, params);
          if (fetchOptions.method === `GET` || queryOpts.forceCache) {
            this.responseCache.set(cacheParams, processed);
          }
          return processed;
        })
      );
    });
  }
}
